import type { Viewport } from "@xyflow/react";
import { readPerfDebugFlag } from "@/desktop/perf-debug-flags";
import { requestNativeGlassRefresh } from "@/desktop/native-glass-settings";
import { sharpZoomCompatibleWithNativeGlass } from "@/lib/zoom-rect-semantics";
import { createSharpZoomSettle } from "./webkit-sharp-zoom-settle";

const SHARP_ZOOM_LOG_INTERVAL_MS = 250;
let lastSharpZoomLogAt = 0;
let lastSharpZoomLogSignature = "";

/** WebKit rasterizes React Flow's transformed viewport at the
 *  pre-transform resolution in some cases, so `transform: scale()` can
 *  make text and frame chrome look like a low-res bitmap at zoom > 1.
 *  We use a CSS `zoom:` presentation path for the steady WebKit
 *  viewport: React Flow still owns the canonical x/y/zoom state, while
 *  WebKit paints zoomed-in content at device-pixel sharpness.
 *
 *  Tauri on macOS is WKWebView, so it needs the same sharpness path as
 *  Safari. The important constraint is to toggle this path only when zoom
 *  actually changes; clicks, drags, selection, and pan should never flicker
 *  through the blurry transform-scaled presentation.
 *  Under Tauri the workaround additionally requires rect semantics the
 *  native-glass sync can compensate (see lib/zoom-rect-semantics.ts) —
 *  otherwise we fall back to transform: scale() so AppKit plates stay
 *  aligned with the DOM. */
const useCssZoomWorkaround =
  typeof navigator !== "undefined" &&
  /AppleWebKit/i.test(navigator.userAgent) &&
  !/Chrome|Chromium|Edg|Android/i.test(navigator.userAgent);

const frameIntersectsWindow = (frame: Element) => {
  const rect = frame.getBoundingClientRect();
  return (
    rect.width >= 8 &&
    rect.height >= 8 &&
    rect.right > 0 &&
    rect.bottom > 0 &&
    rect.left < window.innerWidth &&
    rect.top < window.innerHeight
  );
};

function maybeLogSharpZoomFrameCounts(el: HTMLElement, zoom: number) {
  if (
    !readPerfDebugFlag("sharpZoomFrameCounts") ||
    typeof performance === "undefined"
  ) {
    return;
  }
  const now = performance.now();
  if (now - lastSharpZoomLogAt < SHARP_ZOOM_LOG_INTERVAL_MS) return;

  const frames = Array.from(el.querySelectorAll(".react-flow__node"));
  const visible = frames.filter(frameIntersectsWindow).length;
  const zoomBucket = Math.round(zoom * 20) / 20;
  const signature = `${frames.length}:${visible}:${zoomBucket}`;
  if (signature === lastSharpZoomLogSignature) return;

  lastSharpZoomLogAt = now;
  lastSharpZoomLogSignature = signature;
  console.log(
    `[perf] sharp-zoom-frames rendered=${frames.length} visible=${visible} culled=${Math.max(0, frames.length - visible)} zoom=${zoom.toFixed(2)}`,
  );
}

function applyWebkitSharpZoom(el: HTMLElement, vp: Viewport) {
  if (
    !useCssZoomWorkaround ||
    vp.zoom <= 1 ||
    !sharpZoomCompatibleWithNativeGlass()
  ) {
    el.classList.remove("safari-zoom-active");
    return;
  }

  el.style.setProperty("--rf-tx", `${vp.x}px`);
  el.style.setProperty("--rf-ty", `${vp.y}px`);
  if (el.style.getPropertyValue("--rf-zoom") !== String(vp.zoom)) {
    el.style.setProperty("--rf-zoom", String(vp.zoom));
  }
  el.classList.add("safari-zoom-active");
  maybeLogSharpZoomFrameCounts(el, vp.zoom);
}

const webkitSharpZoomSettle = createSharpZoomSettle({
  isEnabled: () => useCssZoomWorkaround && readPerfDebugFlag("sharpZoomSettle"),
  apply: applyWebkitSharpZoom,
  suspend: (el) => el.classList.remove("safari-zoom-active"),
  onSettled: () => requestNativeGlassRefresh({ trackTransition: false }),
});

/** Present viewport `vp` on the canvas container `el` with WebKit's sharp
 *  CSS `zoom` path where it applies (see {@link useCssZoomWorkaround}). */
export function syncWebkitSharpZoom(el: HTMLElement | null, vp: Viewport) {
  if (el) webkitSharpZoomSettle.sync(el, vp);
}

/**
 * The CSS `zoom` this path applies to `element`: the canvas zoom inside a
 * sharp-zoomed viewport, 1 on any other surface (the wall, focus windows).
 */
export function sharpZoomOf(element: Element): number {
  const container = element.closest<HTMLElement>(".safari-zoom-active");
  const viewport = container?.querySelector(".react-flow__viewport");
  if (!container || !viewport?.contains(element)) return 1;
  const zoom = Number.parseFloat(container.style.getPropertyValue("--rf-zoom"));
  return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
}

/** Drops a pending sharp-zoom settle, e.g. when the canvas unmounts. */
export const cancelWebkitSharpZoomSettle = () => webkitSharpZoomSettle.cancel();
